import React, { useEffect, useMemo, useState } from "react";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { Pencil, Search, X } from "lucide-react";

import Select from "react-select";

import BreadCrumb from "common/breadcrumb";
import TableContainer from "common/table-container";

import LoadingComponent from "common/components/loader";

import { BrandingData } from "admin-portal/settings/branding";

import { Service } from "../../models/setup/service";
import { CreateService, GetServices, GetServiceTypes, GetTaxes, UpdateService } from "api/setup";
import { getAuthState } from "slices/auth/login/reducer";
import { useAppSelector } from "slices/hook";
import { TenantType } from "models/enums";
import { Tenant } from "models/tenant/tenant";
import { ServiceType } from "models/setup/service-type";
import { Button } from "@headlessui/react";
import { label } from "yet-another-react-lightbox/*";
import { Tax } from "models/tax/tax";
import { AppUtility } from "utility/app-utility";

interface IService {
  id?: number;
  name?: string;
  isActive?: boolean;
  tenant?: { value: string; label: string };
  serviceType?: { value: string; label: string };
  parentService?: { value: string; label: string };
  iconBase64?: string;
  iconUrl?: string;

  cost?: string;
  fee?: string;
  tax?: { value: string; label: string };

  priorityChargeFixed?: string;
  priorityChargePecentage?: string;

  isDiscountPercentage?: boolean;
  discountAmount?: string;
  estimatedTimeInMinutes?: string;
}

const ServicesPage: React.FC = () => {
  const title = "Services";
  const pageTitle = "Setup";

  const [loading, setLoading] = useState(false);
  const [jobCategories, setJobCategories] = useState<Service[]>([]);

  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState<IService | undefined>();
  const [editMode, setEditMode] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [parentServices, setParentServices] = useState<{ value: string; label: string }[]>([]);
  const [serviceTypes, setServiceTypes] = useState<{ value: string; label: string }[]>([]);
  const [taxes, setTaxes] = useState<{ value: string; label: string }[]>([]);

  const { loggedInuser } = useAppSelector(getAuthState);
  const [tenants, setTenants] = useState<{ value: string; label: string }[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<{ value: string; label: string }>();

  useEffect(() => {
    loadServiceTypes();
    loadTaxes();

    let _tenants = loggedInuser?.tenants
      ?.filter((t) => t.tenantTypeId != TenantType.SYSTEM)
      .map((t, index) => ({ value: t.id ?? "", label: t.companyName ?? "" }));
    if (_tenants) {
      _tenants = _tenants.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      setTenants([{ label: "Select", value: "" }, ..._tenants]);
    }
    loadData();
  }, []);

  const loadServiceTypes = async () => {
    const res = await GetServiceTypes();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.name ?? "" }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setServiceTypes(_st);
    } else {
      setServiceTypes([]);
    }
  };

  const loadTaxes = async () => {
    const res = await GetTaxes();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.name ?? "" }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setTaxes(_st);
    } else {
      setTaxes([]);
    }
  };
  const loadData = async () => {
    //param?: TenantSearchParam
    setLoading(true);
    const param: any = {};
    if ((selectedTenant?.value ?? "") != "") {
      param["tenantId"] = selectedTenant?.value ?? "";
    }
    const res = await GetServices(param);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setJobCategories(res?.data ?? []);
      setTotal(res?.data?.length ?? 0);
      setParentServices([...(res?.data ?? [])].map((t, index) => ({ value: t.id ?? "", label: t.name ?? "" })));
    } else {
      setJobCategories([]);
      setTotal(0);
      setParentServices([]);
    }
    setLoading(false);
  };

  /*   useEffect(() => {
    console.log("Service Types:", serviceTypes);
    console.log("Taxes:", taxes);
    console.log("Tenants:", tenants);
  }, [tenants]); */

  const handleEditClick = (
    c: Service,
    tnt: { value: string; label: string }[],
    st: { value: string; label: string }[],
    tx: { value: string; label: string }[]
  ) => {
    // const _edited = jobCategories.find((d) => d.id == c);
    const _selected = {
      id: c?.id,
      name: c?.name ?? "",
      isActive: c?.isActive,
      tenant: tnt.find((t) => t.value == c?.tenant?.id),
      serviceType: st.find((t) => t.value == (c?.serviceType?.id ?? 0).toString()),
      parentService: parentServices.find((t) => t.value == (c?.parentService?.id ?? 0).toString()),
      cost: (c?.cost ?? "").toString(),
      fee: (c?.fee ?? "").toString(),
      tax: tx.find((t) => t.value == (c?.tax?.id ?? 0).toString()),

      priorityChargeFixed: (c?.priorityChargeFixed ?? "").toString(),
      priorityChargePecentage: (c?.priorityChargePecentage ?? "").toString(),
      isDiscountPercentage: c?.discountPercentage != undefined && c?.discountPercentage != null,
      discountAmount: (
        (c?.discountPercentage != undefined && c?.discountPercentage != null
          ? c?.discountPercentage
          : c?.discountAmount) ?? ""
      ).toString(),

      iconUrl: c.iconUrl,
      estimatedTimeInMinutes: (c.estimatedTimeInMinutes ?? "").toString(),
    };
    setSelected(_selected);
    setEditMode(true);
    formik.setValues(_selected); // Set formik values when prop changes
  };
  const handleResetClick = () => {
    setSelected(undefined);
    setEditMode(false);
    formik.setValues({
      name: "",
      isActive: true,
      tenant: null,
      serviceType: null,
      parentService: null,
      cost: "",
      fee: "",
      tax: null,

      priorityChargeFixed: "",
      priorityChargePecentage: "",
      isDiscountPercentage: true,
      discountAmount: "",

      iconUrl: "",
      estimatedTimeInMinutes: "",
    }); // Set formik values when prop changes
  };
  const columns = useMemo(
    () => [
      {
        header: `Service Type`,
        accessorKey: "",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const { serviceType, id } = cell.row.original;

          return <span>{serviceType.name}</span>;
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: `${BrandingData.bussinessDomain}`,
        accessorKey: "",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const { tenant, id } = cell.row.original;

          return <span>{tenant.companyName}</span>;
        },
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const { isActive, id } = cell.row.original;

          return (
            <div className="flex items-center">
              <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                <input
                  type="checkbox"
                  id={`status-${id}`} // unique ID for each row
                  checked={isActive}
                  readOnly // making checkbox read-only to avoid user interaction
                  className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                />
                <label
                  htmlFor={`status-${id}`}
                  className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer transition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                ></label>
              </div>
            </div>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="flex justify-end gap-2">
            <div
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400 cursor-pointer"
              onClick={() => handleEditClick(cell.row.original, tenants, serviceTypes, taxes)}
            >
              {/*  */}
              <Pencil className="size-4" />
            </div>
          </div>
        ),
      },
    ],
    [tenants, serviceTypes, taxes]
  );

  //Add

  const formik: any = useFormik({
    initialValues: {
      name: "",
      isActive: true,
      tenant: null,
      serviceType: null,
      parentService: null,

      cost: "",
      fee: "",
      tax: null,

      priorityChargeFixed: "",
      priorityChargePecentage: "",
      isDiscountPercentage: true,
      discountAmount: "",

      iconUrl: "",
      estimatedTimeInMinutes: "30",
      ...selected, // Spread the fetched selected  data if available
    },
    validationSchema: Yup.object().shape({
      tenant: Yup.object()
        .shape({
          label: Yup.string().required(`${BrandingData.bussinessDomain} is required`),
          value: Yup.string(),
        })
        .required(`A ${BrandingData.bussinessDomain.toLocaleLowerCase()} must be selected`),
      serviceType: Yup.object()
        .shape({
          label: Yup.string().required(`Service type is required`),
          value: Yup.string(),
        })
        .required(`A service type must be selected`),
      name: Yup.string().required("Name is required"),
      cost: Yup.number()
        .nullable() // Allows the field to be null or empty
        .typeError("Must be a valid number") // Ensures input is a number
        .test("is-decimal-or-integer", "Must be a valid number with up to two decimal places", (val) => {
          var value = (val ?? "").toString();
          if (value === null || value === "") {
            return true; // Allow empty values
          }
          // Check if it's a valid number with up to two decimal places
          return /^\d+(\.\d{1,2})?$/.test(value);
        }),
      fee: AppUtility.formikCurrencyValidtion(),
      priorityChargeFixed: AppUtility.formikCurrencyValidtion(),
      priorityChargePecentage: AppUtility.formikCurrencyValidtion(),
      discountAmount: AppUtility.formikCurrencyValidtion(),
    }),

    onSubmit: async (values) => {
      try {
        //setError("");
        //  setSuccess("");
        setSaving(true);

        var _object: Service = {
          id: selected?.id ?? undefined,
          name: values.name ?? "",
          isActive: values.isActive ?? true,
          //tenantId: values.tenant?.value ?? "",
          tenant: {
            id: values.tenant?.value ?? "",
            companyName: values.tenant?.label ?? "",
          },
          serviceType: {
            id: parseInt(values.serviceType?.value ?? "0"),
            name: values.serviceType?.label ?? "",
          },
          parentService: {
            id: parseInt(values.parentService?.value ?? "0"),
            name: values.parentService?.label ?? "",
          },

          cost: values.cost ? parseFloat(values.cost) : undefined,
          fee: values.fee ? parseFloat(values.fee) : undefined,
          tax: {
            id: parseInt(values.tax?.value ?? "0"),
            name: values.tax?.label ?? "",
          },

          priorityChargeFixed: values.priorityChargeFixed ? parseFloat(values.priorityChargeFixed) : undefined,
          priorityChargePecentage: values.priorityChargePecentage
            ? parseFloat(values.priorityChargePecentage)
            : undefined,
          discountPercentage: values.isDiscountPercentage
            ? values.discountAmount ?? undefined
              ? parseFloat(values.discountAmount)
              : undefined
            : undefined,
          discountAmount: !values.isDiscountPercentage
            ? values.discountAmount ?? undefined
              ? parseFloat(values.discountAmount)
              : undefined
            : undefined,

          iconUrl: values.iconUrl,
          estimatedTimeInMinutes: parseFloat(values.estimatedTimeInMinutes),
        };
        const res = (selected?.id ?? 0) == 0 ? await CreateService(_object) : await UpdateService(_object);

        if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
          setSuccess("success");
          setSelected(undefined);
          loadData();
          formik.resetForm();

          // setEditMode(true);
        } else {
          setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
        }
        setTimeout(() => {
          setError("");
          setSuccess("");
        }, 3000);
        setSaving(false);
      } catch (error) {
        setError("Something went wrong...");
        setSaving(false);
      }
    },
  });

  return (
    <React.Fragment>
      <BreadCrumb title={title} pageTitle={pageTitle} />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5 min-w-[50%]">
        <div className="xl:col-span-12">
          <div className="card">
            <div className="card-body">
              <h6 className="mb-4 text-15"> {(selected?.id ?? "") == "" ? "Add" : "Update"} Service</h6>

              <form
                action="#!"
                id="jobCategoryForm"
                onSubmit={(event: any) => {
                  event.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <>
                  <div className="grid grid-cols-1 gap-x-5">
                    <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="fromInpuTenant" className="inline-block mb-2 text-base font-medium">
                          {BrandingData.bussinessDomain} <span className="text-red-500">*</span>
                        </label>
                        <Select
                          className="border-slate-200 focus:outline-none focus:border-custom-500"
                          options={tenants}
                          isSearchable={false} // To disable search
                          name="tenant"
                          id="tenant"
                          value={formik.values.tenant}
                          onChange={(option) => formik.setFieldValue("tenant", option)}
                        />
                        {formik.touched.tenant && formik.errors.tenant ? (
                          <p id="tenant" className="mt-1 text-sm text-red-500">
                            {formik.errors.tenant.label || formik.errors.tenant}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="nameInput" className="inline-block mb-2 text-base font-medium">
                          Service/Sub Service Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.name && formik.errors.name ? (
                          <p id="name" className="mt-1 text-sm text-red-500">
                            {formik.errors.name}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="fromInputServiceType" className="inline-block mb-2 text-base font-medium">
                          Service Type <span className="text-red-500">*</span>
                        </label>
                        <Select
                          className="border-slate-200 focus:outline-none focus:border-custom-500"
                          options={serviceTypes}
                          isSearchable={false} // To disable search
                          name="serviceType"
                          id="serviceType"
                          value={formik.values.serviceType} // Set Formik's `serviceType` value
                          onChange={(option) => formik.setFieldValue("serviceType", option)} // Update Formik state
                        />
                        {formik.touched.serviceType && formik.errors.serviceType ? (
                          <p id="serviceType" className="mt-1 text-sm text-red-500">
                            {formik.errors.serviceType.label || formik.errors.serviceType}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="fromInputParentService" className="inline-block mb-2 text-base font-medium">
                          Primary Service <span className="text-red-500"></span>
                        </label>
                        <Select
                          className="border-slate-200 focus:outline-none focus:border-custom-500"
                          options={parentServices}
                          isSearchable={false} // To disable search
                          name="parentService"
                          id="parentService"
                          value={formik.values.parentService} // Set Formik's `serviceType` value
                          onChange={(option) => formik.setFieldValue("parentService", option)} // Update Formik state
                        />
                        {formik.touched.parentService && formik.errors.parentService ? (
                          <p id="parentService" className="mt-1 text-sm text-red-500">
                            {formik.errors.parentService.label || formik.errors.parentService}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="costInput" className="inline-block mb-2 text-base font-medium">
                          Cost
                        </label>
                        <input
                          type="text"
                          name="cost"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Cost"
                          value={formik.values.cost}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.cost && formik.errors.cost ? (
                          <p id="cost" className="mt-1 text-sm text-red-500">
                            {formik.errors.cost}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="feeInput" className="inline-block mb-2 text-base font-medium">
                          Fee
                        </label>
                        <input
                          type="text"
                          name="fee"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Fee"
                          value={formik.values.fee}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.fee && formik.errors.fee ? (
                          <p id="fee" className="mt-1 text-sm text-red-500">
                            {formik.errors.fee}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="taxInput" className="inline-block mb-2 text-base font-medium">
                          Tax
                        </label>
                        <Select
                          className="border-slate-200 focus:outline-none focus:border-custom-500"
                          options={taxes}
                          isSearchable={false} // To disable search
                          name="tax"
                          id="tax"
                          value={formik.values.tax} // Set Formik's `serviceType` value
                          onChange={(option) => formik.setFieldValue("tax", option)} // Update Formik state
                        />
                        {formik.touched.tax && formik.errors.tax ? (
                          <p id="serviceType" className="mt-1 text-sm text-red-500">
                            {formik.errors.tax.label || formik.errors.tax}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="priorityChargeFixedInput" className="inline-block mb-2 text-base font-medium">
                          Priority Charge
                        </label>
                        <input
                          type="text"
                          name="priorityChargeFixed"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="priorityChargeFixed"
                          value={formik.values.priorityChargeFixed}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.priorityChargeFixed && formik.errors.priorityChargeFixed ? (
                          <p id="fee" className="mt-1 text-sm text-red-500">
                            {formik.errors.priorityChargeFixed}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label
                          htmlFor="priorityChargePecentageInput"
                          className="inline-block mb-2 text-base font-medium"
                        >
                          Priority Charge Percentage
                        </label>
                        <input
                          type="text"
                          name="priorityChargePecentage"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="priorityChargePecentage"
                          value={formik.values.priorityChargePecentage}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.priorityChargePecentage && formik.errors.priorityChargePecentage ? (
                          <p id="fee" className="mt-1 text-sm text-red-500">
                            {formik.errors.priorityChargePecentage}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <div>
                          <label htmlFor="isDiscountPercentage" className="inline-block mb-2 text-base font-medium">
                            Discount Percentage
                          </label>
                          <div className="flex items-center flex-row">
                            <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                              <input
                                type="checkbox"
                                name="isDiscountPercentage"
                                id="isDiscountPercentage"
                                className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                                onChange={formik.handleChange}
                                checked={formik.values.isDiscountPercentage}
                              />
                              <label
                                htmlFor="isDiscountPercentage"
                                className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                              ></label>
                            </div>
                            <input
                              type="text"
                              name="discountAmount"
                              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                              placeholder="Value"
                              value={formik.values.discountAmount}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            {formik.touched.discountAmount && formik.errors.discountAmount ? (
                              <p id="discountAmount" className="mt-1 text-sm text-red-500">
                                {formik.errors.discountAmount}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="estimatedTimeInMinutes" className="inline-block mb-2 text-base font-medium">
                          Estimate Time in Minutes
                        </label>
                        <input
                          type="text"
                          name="estimatedTimeInMinutes"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="estimatedTimeInMinutes"
                          value={formik.values.estimatedTimeInMinutes}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.estimatedTimeInMinutes && formik.errors.estimatedTimeInMinutes ? (
                          <p id="estimatedTimeInMinutes" className="mt-1 text-sm text-red-500">
                            {formik.errors.estimatedTimeInMinutes}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="iconInput" className="inline-block mb-2 text-base font-medium">
                          Icon
                        </label>
                        <input
                          type="text"
                          name="iconUrl"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="iconUrl"
                          value={formik.values.iconUrl}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.iconUrl && formik.errors.iconUrl ? (
                          <p id="iconUrl" className="mt-1 text-sm text-red-500">
                            {formik.errors.iconUrl}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <div>
                          <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                            Active
                          </label>
                          <div className="flex items-center">
                            <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                              <input
                                type="checkbox"
                                name="isActive"
                                id="isActive"
                                className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                                onChange={formik.handleChange}
                                checked={formik.values.isActive}
                              />
                              <label
                                htmlFor="isActive"
                                className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                              ></label>
                            </div>
                          </div>
                        </div>

                        {formik.touched.isActive && formik.errors.isActive ? (
                          <p id="isActive" className="mt-1 text-sm text-red-500">
                            {formik.errors.isActive}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 mt-5">
                    <button
                      type="button"
                      onClick={handleResetClick}
                      className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                    >
                      <X className="inline-block size-4" />
                      <span className="align-middle">Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    >
                      {(selected?.id ?? "") == "" ? "Create" : "Update"}
                    </button>
                  </div>
                </>
              </form>
              {saving && <LoadingComponent />}
              {success && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
                  id="successAlert"
                >
                  <b>Success! </b>
                  {<>Service has been {!editMode ? "added" : "updated"} successfully</>}
                </div>
              )}
              {error && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
                  id="successAlert"
                >
                  <>
                    Oops! Something went wrong while attempting to {!editMode ? "add" : "update"} service. Please try
                    again later or contact support for assistance.
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="xl:col-span-12">
          <div className="card">
            <div className="card-body">
              {/*   <h6 className="mb-4 text-15">Service Type</h6> */}

              <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 xl:grid-cols-12">
                <div className="xl:col-span-3">{BrandingData.bussinessDomain}</div>
                <div className="xl:col-span-6">
                  <Select
                    className="border-slate-200 focus:outline-none focus:border-custom-500"
                    options={tenants}
                    isSearchable={false} // To disable search
                    name="tenantS"
                    id="tenantS"
                    value={selectedTenant}
                    onChange={(option) => {
                      setSelectedTenant({ label: option?.label ?? "", value: option?.value ?? "" });
                    }}
                  />
                </div>

                <div className="xl:col-span-3">
                  <Button
                    type="button"
                    className="text-white btn min-w-40
                  bg-custom-500 border-custom-500 
                  hover:text-white hover:bg-custom-600 hover:border-custom-600 
                  focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring 
                  focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 
                  active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={() => {
                      loadData();
                    }}
                  >
                    <Search className="inline-block size-4" /> <span className="align-middle">Search</span>
                  </Button>
                </div>
                <div className="xl:col-span-12">
                  <div className="mx-auto w-full">
                    {loading ? (
                      <LoadingComponent></LoadingComponent>
                    ) : (
                      <div className="overflow-x-auto mt-2">
                        {jobCategories && jobCategories.length > 0 ? (
                          <TableContainer
                            isPagination={true}
                            columns={columns || []}
                            data={jobCategories || []}
                            totalCount={total}
                            customPageSize={10}
                            divclassName="overflow-x-auto"
                            tableclassName="w-full whitespace-nowrap"
                            theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                            thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                            tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                            PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                          />
                        ) : (
                          <div className="noresult">
                            <div className="py-6 text-center">
                              {/*   <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" /> */}
                              <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                              <p className="mb-0 text-slate-500 dark:text-zink-200">
                                No services setup for this {BrandingData.bussinessDomain.toLocaleLowerCase()}, please
                                create new service.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
export default ServicesPage;
